/**
 * `docs/prompts/02-client.md`: "Fixed-timestep game loop: accumulate real
 * elapsed ms, step the engine in whole ticks, render on rAF. Pause on
 * `visibilitychange`."
 *
 * `TICK_MS` (from `@interlock/engine`) is 1000/60. Real frame time
 * accumulates into a bucket; whole ticks are drained from it, so the engine
 * always advances in exact 1/60s units no matter what the display's refresh
 * rate is — a 120Hz phone still produces the same sequence of ticks a 60Hz
 * monitor does, which is what makes the replay and the hash mean anything
 * (`docs/decisions/0002`).
 *
 * A tick is `@interlock/sim`'s `stepWithRule`: the mode's tick rule, if it
 * has one, then the engine's `step`. That is the same rule in the same place
 * an online board runs it, so survival's floor rises on the same ticks solo
 * as it does in a duel (`docs/decisions/0050`).
 */
import { TICK_MS } from '@interlock/engine';
import type { GameState, InputAction, InputEvent, StepResult } from '@interlock/engine';
import { stepWithRule } from '@interlock/sim';
import type { TickRule } from '@interlock/sim';

/** Clamp a single frame's real elapsed time so a long stall (a dev-tools breakpoint, a backgrounded tab) cannot fast-forward the game on return. */
const MAX_FRAME_DT_MS = 250;

export interface GameLoopCallbacks {
  /**
   * The mode's rule for the start of every tick (`ModeDescriptor.tickRule`),
   * when it has one. Applied before the tick's inputs.
   */
  readonly tickRule?: TickRule;
  /** Actions to apply on the tick about to run. Called once per tick, immediately before the tick is stepped. */
  readonly collectActions: () => readonly InputAction[];
  /** Called after every tick with that tick's result. */
  readonly onTick: (result: StepResult, tickMs: number) => void;
  /** Called once per animation frame, after any ticks this frame produced. */
  readonly onRender: () => void;
}

export interface GameLoopScheduler {
  readonly requestFrame: (callback: (time: number) => void) => number;
  readonly cancelFrame: (handle: number) => void;
}

const defaultScheduler: GameLoopScheduler = {
  requestFrame: (cb) => requestAnimationFrame(cb),
  cancelFrame: (handle) => {
    cancelAnimationFrame(handle);
  },
};

export class GameLoop {
  private rafHandle: number | null = null;
  private lastFrameTime: number | null = null;
  private accumulatorMs = 0;
  private running = false;

  constructor(
    private readonly state: GameState,
    private readonly callbacks: GameLoopCallbacks,
    private readonly scheduler: GameLoopScheduler = defaultScheduler,
  ) {}

  get isRunning(): boolean {
    return this.running;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrameTime = null;
    this.rafHandle = this.scheduler.requestFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafHandle !== null) {
      this.scheduler.cancelFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  /** Wire directly to `document.visibilitychange`: pauses on hide, resumes on show. */
  handleVisibilityChange(visibilityState: DocumentVisibilityState): void {
    if (visibilityState === 'hidden') {
      this.stop();
    } else if (!this.running) {
      this.start();
    }
  }

  private readonly frame = (time: number): void => {
    if (!this.running) return;

    const last = this.lastFrameTime ?? time;
    const dt = Math.min(MAX_FRAME_DT_MS, Math.max(0, time - last));
    this.lastFrameTime = time;
    this.accumulatorMs += dt;

    while (this.accumulatorMs >= TICK_MS && this.running) {
      this.accumulatorMs -= TICK_MS;
      const actions = this.callbacks.collectActions();
      const events: InputEvent[] = actions.map((action) => ({ frame: this.state.tick, action }));
      const result = stepWithRule(this.state, this.callbacks.tickRule ?? null, events);
      this.callbacks.onTick(result, TICK_MS);
      if (result.topOut) {
        this.accumulatorMs = 0;
        break;
      }
    }

    this.callbacks.onRender();

    if (this.running) this.rafHandle = this.scheduler.requestFrame(this.frame);
  };
}
